/* eslint-disable max-classes-per-file */
import { Grid } from '@mui/material';
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { v4 as uuid } from 'uuid';
import BlockProps from '../../../models/BlockProps';
import BlockData from '../../../models/BlockData';
import LinkData from '../../../models/LinkData';
import BlockFooterData from './BlockFooterData';
import BlockFooterMenuSettings from './BlockFooterMenuSettings';
import BlockComponentImage from '../../../models/BlockComponentValues/BlockComponentImage';
import useGetBackground from '../../../hooks/UseGetBackground';
import BackGroundImageModal from '../../Modals/ImageModal/BackGroundImageModal';
import useParallaxScript from '../../../hooks/useParallaxScript';
import config from '../../../config';
import FooterMenu from './shared/FooterMenu';
import Utils from '../../../utils/Utils';
import CloseButton from '../../Buttons/Close';
import EditableText from '../../Form/EditableText';
import { BlockJustify, BlockSeparator } from '../../../models/BlockSettings';

export interface BlockFooterProps extends BlockProps {
  blockData: BlockData[],
}

function BlockFooter({
  data, isEditing, isTemplate, editHandler, blockData, showImageModal, onCloseImageModal,
}: BlockFooterProps) {
  const [props, setProps] = useState<BlockFooterData>(data.data as BlockFooterData);

  useEffect(() => {
    setProps(data.data as BlockFooterData);
  }, [data.data]);

  useEffect(() => {
    if (!props.settings?.justify) {
      setProps({ ...props, settings: { ...props.settings, justify: BlockJustify.CENTER } });
    }

    if (!props.settings?.separator) {
      setProps({ ...props, settings: { ...props.settings, separator: BlockSeparator.NONE } });
    }
  }, [props]);

  const [open, setOpen] = useState(false);

  const handleOpen = useCallback(() => {
    if (isEditing && !isTemplate) {
      setOpen(true);
    }
  }, [isEditing, isTemplate]);
  const handleClose = () => setOpen(false);

  const handleUpdate = (text: string) => {
    props.title.value = text;
    editHandler(data.id, props);
  };

  const handleMenuDelete = useCallback(() => {
    props.menus = undefined;
    editHandler(data.id, props);
  }, [editHandler, data.id, props]);

  const menuItems = useMemo(() => {
    if (props.menus && props.menus?.length > 0) {
      return (
        <Grid
          container
          id="footerMenu"
          className="footerMenu"
          justifyContent="center"
          alignItems="center"
        >
          <FooterMenu
            menus={props.menus}
            footerSettings={props.settings}
            isEditing={isEditing}
            handleMenuDelete={handleMenuDelete}
            handleOpen={handleOpen}
          />
        </Grid>
      );
    }
    return [];
  }, [props.menus, props.settings, isEditing, handleMenuDelete, handleOpen]);

  const elementUuid = uuid();
  useParallaxScript(props.url?.settings?.parallaxBool, elementUuid, config.backgroundImage.parallaxEffect);

  return (
    <Grid
      id={elementUuid}
      container
      spacing={0}
      direction="column"
      alignItems="center"
      justifyContent="center"
      style={{
        overflow: 'hidden',
        ...useGetBackground(
          props.url?.solidColor,
          props.url?.value,
          props.url?.settings?.scale,
          props.url?.settings?.scaleBool,
        ),
        backgroundPosition: 'center 0px',
      }}
    >
      {(isEditing && !isTemplate) && (
        <>
          <BackGroundImageModal
            backgroundImage={props.url}
            isShowModal={showImageModal}
            onCloseModal={onCloseImageModal}
            handleOnConfirm={(url: BlockComponentImage) => {
              const tempBg = { ...props, url: { ...url } };
              setProps(tempBg);
              editHandler(data.id, tempBg);
            }}
          />
        </>
      )}

      <Grid container justifyContent="center" alignItems="center" sx={{ p: 1.5 }}>
        {menuItems}

        {props.title && (
          <Grid item sx={Utils.hoverBorder(isEditing)}>
            {isEditing && (
              <CloseButton updateHandler={() => {
                props.title = undefined;
                editHandler(data.id, props);
              }}
              />
            )}
            <EditableText
              text={props.title?.value}
              textColor={props.title?.settings?.textColor}
              isEditable={isEditing && !isTemplate}
              updateHandler={handleUpdate}
            />
          </Grid>
        )}
      </Grid>

      <BlockFooterMenuSettings
        menuItems={props.menus ?? []}
        settings={props.settings}
        open={open}
        closeHandler={(newItems?: LinkData[]) => {
          if (newItems) {
            props.menus = newItems;
            editHandler(data.id, props);
          }
          handleClose();
        }}
        updateHandler={(footerSettings: any) => {
          props.settings = footerSettings;
        }}
        blockData={blockData}
      />
    </Grid>
  );
}

export default BlockFooter;
